import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { checkConnection, fetchConnection, clearSyncProgress } from '@/lib/api';
import type { ConnectionStatus } from '@/types';

/**
 * Live Shopify connection check. Re-runs every time the Settings page mounts so
 * the page always shows the real connection state, never a stale database row.
 */
export function useConnectionCheck() {
  return useQuery({
    queryKey: ['connection-check'],
    queryFn: checkConnection,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: false,
  });
}

export function useConnection() {
  return useQuery({
    queryKey: ['connection'],
    queryFn: async () => {
      const conn = await fetchConnection();
      if (conn?.sync_progress?.phase) {
        console.log(
          `[Sync] phase=${conn.sync_progress.phase} fetched=${conn.sync_progress.fetched ?? '?'} started=${conn.sync_progress.startedAt ?? '?'}`,
        );
      }
      return conn;
    },
    refetchInterval: (query) => {
      const conn = query.state.data;
      // Poll every 3s while a sync is in progress
      return conn?.sync_progress?.phase && (!conn.sync_progress.status || conn.sync_progress.status === 'running')
        ? 3_000
        : false;
    },
  });
}

export function useClearSyncProgress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearSyncProgress,
    onMutate: async () => {
      // Cancel in-flight polling refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({ queryKey: ['connection'] });
      const previous = queryClient.getQueryData<ConnectionStatus | null>(['connection']);
      queryClient.setQueryData<ConnectionStatus | null>(['connection'], (old) =>
        old ? { ...old, sync_progress: null } : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback on failure
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['connection'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['connection'] });
    },
  });
}
